import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Modal, Button } from '../../components';
import css from './YouTubeModal.module.css';
import IconCard from '../SavedCardDetails/IconCard/IconCard';

const YouTubeModal = ({ isOpen, onClose, onConfirm, onManageDisableScrolling }) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setUrl('');
      setError('');
    }
  }, [isOpen]);

  const handleClose = () => {
    setUrl('');
    setError('');
    onClose();
  };

  const handleSubmit = useCallback((e) => {
    if (e) e.preventDefault();
    
    // Basic URL validation
    if (!url) {
      setError('Please enter a YouTube URL');
      return;
    }

    // Validate YouTube URL format
    const videoIdMatch = url.match(
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
    );

    if (!videoIdMatch || !videoIdMatch[1]) {
      setError('Please enter a valid YouTube URL');
      return;
    }

    onConfirm(url);
    handleClose();
  }, [url, onConfirm, onClose]);

  if (!isOpen) return null;

  return (
    <Modal
      id="youtube-modal"
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      containerClassName={css.modalContainer}
      contentClassName={css.modalContent}
    >
      <div className={css.modalHeader}>
        <h2 className={css.modalTitle}>
          <FormattedMessage id="FieldRichTextEditor.youtubeModal.title" defaultMessage="Embed YouTube video" />
        </h2>
        <button type="button" className={css.closeButton} onClick={handleClose}>
          <IconCard brand="close" />
        </button>
      </div>
      
      <div role="form" className={css.form}>
        <div className={css.formRow}>
          <label htmlFor="youtube-url" className={css.label}>
            <FormattedMessage 
              id="FieldRichTextEditor.youtubeModal.urlLabel" 
              defaultMessage="YouTube video URL" 
            />
          </label>
          <input
            id="youtube-url"
            type="url"
            className={classNames(css.input, { [css.inputError]: error })}
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (error) setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            placeholder="https://www.youtube.com/watch?v=..."
            autoFocus
          />
          {error && <div className={css.error}>{error}</div>}
        </div>
        
        <div className={css.actions}>
          <Button
            type="button"
            onClick={onClose}
            rootClassName={css.cancelButton}
          >
            <FormattedMessage id="FieldRichTextEditor.youtubeModal.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            rootClassName={css.submitButton}
          >
            <FormattedMessage id="FieldRichTextEditor.youtubeModal.insert" defaultMessage="Insert video" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default YouTubeModal;
